import { useEffect, useMemo, useState } from 'react';
import { IconPercentage, IconCash, IconAlertCircle, IconLock } from '@tabler/icons-react';
import Modal from './ui/Modal';
import { useAuth } from '../hooks/useAuth';
import { useManagerApproval } from '../hooks/useManagerApproval';
import { maxDiscountForRole, capLabelForRole } from '../lib/discountCaps';
import { useSettings } from '../hooks/useSettings';

/**
 * DiscountModal — Phase 2.6.
 *
 * Order-level discount before checkout. Two input modes:
 *   - PERCENT  enter %, computed off the cart subtotal
 *   - AMOUNT   enter R value directly
 *
 * Role caps:
 *   CASHIER 10%, SUPERVISOR 20%, MANAGER+ unlimited.
 *
 * If the requested discount exceeds the cashier's cap a manager-PIN
 * step-up is raised before the discount is applied. Server enforces
 * the same gate on place-order (defence in depth).
 *
 * Cart-level only in v1 — line-level discounts come back later if
 * needed.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  subtotalCents: number;            // VAT-inclusive cart total before discount
  initialDiscountCents?: number;
  initialReason?: string;
  onApply: (discountCents: number, reason: string) => void;
}

type Mode = 'PERCENT' | 'AMOUNT';

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

export default function DiscountModal({ open, onClose, subtotalCents, initialDiscountCents, initialReason, onApply }: Props) {
  const { auth } = useAuth();
  const role = auth?.role ?? 'CASHIER';
  const askApproval = useManagerApproval();

  const [mode, setMode] = useState<Mode>('PERCENT');
  const [percentInput, setPercentInput] = useState<string>('');
  const [amountInput, setAmountInput] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMode('PERCENT');
      const initial = initialDiscountCents ?? 0;
      if (initial > 0) {
        setMode('AMOUNT');
        setAmountInput((initial / 100).toFixed(2));
        setPercentInput('');
      } else {
        setPercentInput('');
        setAmountInput('');
      }
      setReason(initialReason ?? '');
      setSubmitting(false);
      setError(null);
    }
  }, [open, initialDiscountCents, initialReason]);

  const discountCents = useMemo(() => {
    if (mode === 'PERCENT') {
      const p = Number(percentInput.replace(',', '.'));
      if (!isFinite(p) || p <= 0) return 0;
      return Math.min(subtotalCents, Math.round((subtotalCents * p) / 100));
    }
    const v = Number(amountInput.replace(',', '.'));
    if (!isFinite(v) || v <= 0) return 0;
    return Math.min(subtotalCents, Math.round(v * 100));
  }, [mode, percentInput, amountInput, subtotalCents]);

  const { settings } = useSettings();
  const overrides = {
    cashierBps: settings.discountCapBpsCashier,
    supervisorBps: settings.discountCapBpsSupervisor,
  };
  const userCap = maxDiscountForRole(role, subtotalCents, overrides);
  // MANAGER+ (cap = 10000bps) is unlimited; no over-cap warning for them.
  const isUnlimitedRole = role === 'MANAGER' || role === 'ADMIN' || role === 'OWNER';
  const overCap = discountCents > userCap && !isUnlimitedRole;
  const newTotal = Math.max(0, subtotalCents - discountCents);
  const canSubmit = !submitting && discountCents > 0 && reason.trim().length > 0;

  async function submit() {
    if (!canSubmit) return;
    setError(null);

    if (overCap) {
      const ok = await askApproval({
        action: `Apply ${fmtR(discountCents)} discount`,
        description: `Your role cap is ${capLabelForRole(role, overrides)} (${fmtR(userCap)}). Manager approval required.`,
      });
      if (!ok) return;
    }

    setSubmitting(true);
    onApply(discountCents, reason.trim());
    setSubmitting(false);
    onClose();
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose} size="lg" title="Apply discount">
      <div className="rounded-lg bg-light border border-border p-4 mb-5">
        <div className="flex items-center justify-between">
          <p className="text-13 font-bold uppercase tracking-wider text-gray-700">Cart subtotal</p>
          <p className="text-18 font-bold text-heading tabular-nums">{fmtR(subtotalCents)}</p>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-12 font-medium text-gray-700">Your cap ({role.toLowerCase()})</p>
          <p className="text-13 font-bold text-heading tabular-nums">{capLabelForRole(role, overrides)} · max {fmtR(userCap)}</p>
        </div>
      </div>

      {/* Mode toggle */}
      <p className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Mode</p>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <button
          type="button"
          onClick={() => setMode('PERCENT')}
          className={[
            'h-14 rounded-lg border-2 flex items-center justify-center gap-2 transition-all',
            mode === 'PERCENT' ? 'bg-primary text-[#FFFFFF] border-primary shadow-md' : 'bg-surface border-border text-heading hover:border-primary',
          ].join(' ')}
        >
          <IconPercentage size={20} stroke={2} />
          <span className="text-14 font-bold">Percent</span>
        </button>
        <button
          type="button"
          onClick={() => setMode('AMOUNT')}
          className={[
            'h-14 rounded-lg border-2 flex items-center justify-center gap-2 transition-all',
            mode === 'AMOUNT' ? 'bg-primary text-[#FFFFFF] border-primary shadow-md' : 'bg-surface border-border text-heading hover:border-primary',
          ].join(' ')}
        >
          <IconCash size={20} stroke={2} />
          <span className="text-14 font-bold">Amount (R)</span>
        </button>
      </div>

      {/* Input */}
      {mode === 'PERCENT' ? (
        <div className="mb-4">
          <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Percent off</label>
          <input
            type="text"
            inputMode="decimal"
            value={percentInput}
            onChange={(e) => setPercentInput(e.target.value.replace(/[^0-9.,]/g, ''))}
            placeholder="e.g. 10"
            className="w-full h-14 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-22 font-bold tabular-nums text-heading"
          />
          <div className="grid grid-cols-5 gap-2 mt-3">
            {[5, 10, 15, 20, 25].map((pct) => (
              <button
                key={pct}
                type="button"
                onClick={() => setPercentInput(String(pct))}
                className="h-10 rounded-md bg-light border border-border text-14 font-bold text-heading hover:border-primary hover:text-primary transition-colors"
              >
                {pct}%
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-4">
          <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Amount off (R)</label>
          <input
            type="text"
            inputMode="decimal"
            value={amountInput}
            onChange={(e) => setAmountInput(e.target.value.replace(/[^0-9.,]/g, ''))}
            placeholder="0.00"
            className="w-full h-14 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-22 font-bold tabular-nums text-heading"
          />
        </div>
      )}

      {/* Result preview */}
      {discountCents > 0 && (
        <div className="rounded-lg bg-success-soft border border-success/30 p-4 mb-4">
          <div className="flex items-center justify-between">
            <p className="text-12 font-bold uppercase tracking-wider text-success">Discount</p>
            <p className="text-16 font-bold text-success tabular-nums">−{fmtR(discountCents)}</p>
          </div>
          <div className="flex items-center justify-between mt-1">
            <p className="text-12 font-bold uppercase tracking-wider text-heading">New total</p>
            <p className="text-22 font-bold text-heading tabular-nums">{fmtR(newTotal)}</p>
          </div>
        </div>
      )}

      {overCap && (
        <div className="mb-4 rounded-md bg-warning-soft border border-warning/40 p-3 flex items-start gap-2">
          <IconLock size={18} className="text-warning flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-warning">
            This exceeds your cap of {capLabelForRole(role, overrides)}. Manager PIN required.
          </p>
        </div>
      )}

      <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">
        Reason <span className="text-danger">*</span>
      </label>
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. regular customer, marketing promo"
        className="w-full h-12 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-15 font-semibold text-heading mb-4"
      />

      {error && (
        <div className="mb-4 rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        {initialDiscountCents && initialDiscountCents > 0 && (
          <button
            type="button"
            onClick={() => { onApply(0, ''); onClose(); }}
            disabled={submitting}
            className="flex-1 h-12 rounded-md bg-surface border-2 border-danger text-14 font-bold text-danger hover:bg-danger-soft disabled:opacity-50 transition-colors"
          >
            Remove discount
          </button>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="flex-[2] h-12 rounded-md bg-primary text-[#FFFFFF] text-15 font-bold hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md"
        >
          {submitting ? 'Applying…' : `Apply ${discountCents > 0 ? `−${fmtR(discountCents)}` : 'discount'}`}
        </button>
      </div>
    </Modal>
  );
}
